import { OfflineVault, type OfflineIdentity } from './offline';
import { canonical, sendOfflineBatch, type OfflineBatch } from './offlineAdapter';
import { sampleServerClock, type ClockSample } from './offlineClock';
import { checkOfflineProvisioning, type EpisodeSnapshot } from './offlineContext';
import { activeEvents } from './projection';
import type { ClinicalEvent } from './types';
import { prenatalDefinition } from './definitions';
import { eventCatalog } from './catalog.generated';

export interface PreparedResources {
  configuration: Record<string, unknown>;
  definition: typeof prenatalDefinition;
  catalog: typeof eventCatalog;
  checkedAt: string;
}
export interface SyncHealth {
  failures: number;
  nextPullAt: number;
  lastSuccessfulSyncAt?: string;
  offlineSince?: string;
  lastErrorStatus?: number;
}
export interface FeedPage {
  events: ClinicalEvent[];
  next_cursor: number;
  has_more: boolean;
}
const apiPrefix = () => (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
export function mergeEventPage(
  existing: ClinicalEvent[],
  incoming: ClinicalEvent[],
  episodeId: string,
): ClinicalEvent[] {
  const merged = new Map(existing.map((event) => [event.id, event]));
  for (const event of incoming) {
    if (!event.id || event.episode_id !== episodeId || !Number.isSafeInteger(event.sequence) || event.sequence < 1)
      throw new Error('Event feed identity is invalid.');
    const previous = merged.get(event.id);
    // Immutable event payloads cannot silently change under the same identity.
    if (
      previous &&
      (previous.sequence !== event.sequence ||
        canonical(previous.payload) !== canonical(event.payload) ||
        previous.status !== event.status ||
        previous.root_id !== event.root_id)
    )
      throw new Error('An event changed under an existing identity; review is required.');
    merged.set(event.id, event);
  }
  return [...merged.values()].sort((a, b) => a.sequence - b.sequence);
}
/** Dating commands are immutable; derive cached EDD from the latest accepted active command. */
export function projectEpisodeDating(snapshot: EpisodeSnapshot): EpisodeSnapshot {
  if (!snapshot.context) return snapshot;
  const event = activeEvents(snapshot.events)
    .filter((e) => e.event_type === 'PRENATAL.DATING')
    .sort((a, b) => a.sequence - b.sequence)
    .at(-1);
  if (!event || typeof event.payload.edd !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(event.payload.edd))
    return snapshot;
  const methods: Record<string, string> = { LMP: 'lmp', ART: 'ivf', ULTRASOUND: 'ultrasound', CLINICAL: 'clinical' };
  return {
    ...snapshot,
    context: {
      ...snapshot.context,
      pregnancy: {
        ...snapshot.context.pregnancy,
        edd: event.payload.edd,
        edd_method: methods[String(event.payload.method)] ?? snapshot.context.pregnancy.edd_method,
      },
    },
  };
}
async function authenticatedGet(path: string, token: string | null, request: typeof fetch) {
  if (!token) throw Object.assign(new Error('Sign in to synchronize.'), { status: 401 });
  const started = Date.now();
  const response = await request(`${apiPrefix()}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
    signal: AbortSignal.timeout(20000),
  });
  const completed = Date.now();
  if (!response.ok)
    throw Object.assign(new Error('Server refresh failed; local records are retained.'), { status: response.status });
  return { response, clock: sampleServerClock(response.headers.get('Date'), started, completed) };
}
export async function refreshPreparedResources(
  vault: OfflineVault,
  identity: OfflineIdentity,
  token: string | null,
  request: typeof fetch = fetch,
) {
  const { response, clock } = await authenticatedGet(
    `/api/v1/organizations/${encodeURIComponent(identity.organizationId)}/flowsheets/configuration`,
    token,
    request,
  );
  const configuration = (await response.json()) as Record<string, unknown>;
  if (!Number.isInteger(configuration.version)) throw new Error('Practice configuration version is invalid.');
  const resources: PreparedResources = {
    configuration,
    definition: prenatalDefinition,
    catalog: eventCatalog,
    checkedAt: new Date().toISOString(),
  };
  await vault.putSnapshot('prepared-resources', resources);
  if (clock) await vault.putSnapshot('device-clock', clock);
  return resources;
}
export async function pullEpisode(
  vault: OfflineVault,
  identity: OfflineIdentity,
  episodeId: string,
  token: string | null,
  request: typeof fetch = fetch,
) {
  const key = `episode:${episodeId}`;
  let snapshot = await vault.getSnapshot<EpisodeSnapshot>(key);
  if (!snapshot) throw new Error('Prepare this episode before synchronizing.');
  let after = snapshot.feedCursor ?? 0;
  for (let pages = 0; pages < 1000; pages++) {
    const { response, clock } = await authenticatedGet(
      `/api/v1/organizations/${encodeURIComponent(identity.organizationId)}/flowsheets/episodes/${encodeURIComponent(episodeId)}/events?after=${after}&limit=500`,
      token,
      request,
    );
    const page = (await response.json()) as FeedPage;
    if (
      !Array.isArray(page.events) ||
      !Number.isSafeInteger(page.next_cursor) ||
      page.next_cursor < after ||
      (page.has_more && page.next_cursor <= after) ||
      page.events.some((event) => event.sequence <= after || event.sequence > page.next_cursor)
    )
      throw new Error('Invalid feed cursor; cached records were retained.');
    await vault.updateSnapshot<EpisodeSnapshot>(key, (previous) => {
      if (!previous) throw new Error('Episode cache disappeared.');
      return projectEpisodeDating({
        ...previous,
        events: mergeEventPage(previous.events, page.events, episodeId),
        feedCursor: Math.max(previous.feedCursor ?? 0, page.next_cursor),
        cachedAt: new Date().toISOString(),
        ...(clock ? { clock } : {}),
      });
    });
    if (clock) await vault.putSnapshot('device-clock', clock);
    if (!page.has_more) return;
    after = page.next_cursor;
  }
  throw new Error('Event feed exceeded the safe page limit; resume synchronization.');
}
export async function synchronizePreparedEpisode(
  vault: OfflineVault,
  identity: OfflineIdentity,
  episodeId: string,
  token: string | null,
  options: { automatic?: boolean; request?: typeof fetch } = {},
) {
  const request = options.request ?? fetch;
  const health = await vault.getSnapshot<SyncHealth>('sync-health');
  if (options.automatic && health && health.nextPullAt > Date.now()) return vault.status();
  try {
    const status = await vault.sync<OfflineBatch>(
      async (envelope, hash) => {
        const result = await sendOfflineBatch(envelope, hash, token, request);
        await vault.updateSnapshot<EpisodeSnapshot>(`episode:${envelope.payload.episodeId}`, (snapshot) => {
          if (!snapshot) throw new Error('Prepared episode is unavailable.');
          return projectEpisodeDating({
            ...snapshot,
            events: mergeEventPage(snapshot.events, result.events, envelope.payload.episodeId),
            cachedAt: new Date().toISOString(),
          });
        });
        return result.receipt;
      },
      { automatic: options.automatic },
    );
    if (status.signInRequired) return status;
    await pullEpisode(vault, identity, episodeId, token, request);
    const prepared = await vault.getSnapshot<EpisodeSnapshot>(`episode:${episodeId}`);
    if (prepared?.authorization && Date.now() - Date.parse(prepared.authorization.checkedAt) > 60000) {
      const authorization = await checkOfflineProvisioning(identity.organizationId, identity.userId, token, request);
      await vault.updateSnapshot<EpisodeSnapshot>(`episode:${episodeId}`, (snapshot) => {
        if (!snapshot) throw new Error('Episode unavailable');
        return { ...snapshot, authorization };
      });
    }
    const resources = await vault.getSnapshot<PreparedResources>('prepared-resources');
    if (!resources || Date.now() - Date.parse(resources.checkedAt) > 60000)
      await refreshPreparedResources(vault, identity, token, request);
    await vault.putSnapshot<SyncHealth>('sync-health', {
      failures: 0,
      nextPullAt: 0,
      lastSuccessfulSyncAt: new Date().toISOString(),
    });
    return vault.status();
  } catch (error) {
    const failures = (health?.failures ?? 0) + 1;
    const status = (error as { status?: number })?.status;
    await vault.putSnapshot<SyncHealth>('sync-health', {
      ...health,
      failures,
      nextPullAt: Date.now() + Math.min(300000, 1000 * 2 ** Math.min(failures - 1, 8)),
      lastErrorStatus: status,
      offlineSince: status ? health?.offlineSince : (health?.offlineSince ?? new Date().toISOString()),
    });
    throw error;
  }
}
export function syncClock(vault: OfflineVault) {
  return vault.getSnapshot<ClockSample>('device-clock');
}
